import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, UserCircle, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { apiFetch } from "@/lib/api-fetch";
import { ApiError } from "@/lib/api-error";
import { reportApiError } from "@/lib/report-error";

interface AgencyMember {
  id: string;
  full_name: string | null;
  email: string | null;
  access_profile_name: string | null;
  access_profile_slug: string | null;
}

function fetchAgencyMembers(): Promise<AgencyMember[]> {
  return apiFetch<AgencyMember[]>(`${SUPABASE_URL}agencia/usuarios`);
}

/** Extrai o `detail` do JSON de erro do backend, se houver. */
function extractApiDetail(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.body) {
    try {
      const parsed = JSON.parse(error.body);
      if (typeof parsed?.detail === "string") return parsed.detail;
    } catch {
      // body não é JSON; ignora
    }
  }
  return error instanceof Error ? error.message : fallback;
}

export default function GestaoUsuariosAgencia() {
  const { profileSlug } = useUserPermissions();
  const isAdmin = profileSlug === "admin_agencia";
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State para modais
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMember, setEditingMember] = useState<AgencyMember | null>(null);
  const [deactivatingMember, setDeactivatingMember] = useState<AgencyMember | null>(null);

  // Form state - criar
  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Form state - editar
  const [editName, setEditName] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [isDeactivating, setIsDeactivating] = useState(false);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["agency_members"],
    queryFn: fetchAgencyMembers,
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await apiFetch(`${SUPABASE_URL}agencia/usuarios`, {
        method: "POST",
        body: JSON.stringify({
          email: createEmail,
          password: createPassword,
          full_name: createName,
        }),
      });

      toast({ title: "Sucesso", description: "Membro criado com sucesso!" });
      setShowCreateModal(false);
      setCreateName("");
      setCreateEmail("");
      setCreatePassword("");
      queryClient.invalidateQueries({ queryKey: ["agency_members"] });
    } catch (error) {
      reportApiError(error, { source: "agency_members", kind: "create" });
      if (error instanceof ApiError && error.status === 409) {
        toast({ title: "Erro", description: "Já existe um usuário com este email.", variant: "destructive" });
        return;
      }
      toast({
        title: "Erro",
        description: extractApiDetail(error, "Erro ao criar membro"),
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMember) return;
    setIsEditing(true);
    try {
      const body: Record<string, string> = {};
      if (editName) body.full_name = editName;
      if (editPassword) body.password = editPassword;

      await apiFetch(`${SUPABASE_URL}agencia/usuarios/${editingMember.id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });

      toast({ title: "Sucesso", description: "Membro atualizado com sucesso!" });
      setEditingMember(null);
      queryClient.invalidateQueries({ queryKey: ["agency_members"] });
    } catch (error) {
      reportApiError(error, { source: "agency_members", kind: "update" });
      toast({
        title: "Erro",
        description: extractApiDetail(error, "Erro ao editar membro"),
        variant: "destructive",
      });
    } finally {
      setIsEditing(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivatingMember) return;
    setIsDeactivating(true);
    try {
      await apiFetch(`${SUPABASE_URL}agencia/usuarios/${deactivatingMember.id}`, {
        method: "DELETE",
      });

      toast({ title: "Sucesso", description: "Membro desativado com sucesso!" });
      setDeactivatingMember(null);
      queryClient.invalidateQueries({ queryKey: ["agency_members"] });
    } catch (error) {
      reportApiError(error, { source: "agency_members", kind: "deactivate" });
      toast({
        title: "Erro",
        description: extractApiDetail(error, "Erro ao desativar membro"),
        variant: "destructive",
      });
    } finally {
      setIsDeactivating(false);
    }
  };

  const openEditModal = (member: AgencyMember) => {
    setEditingMember(member);
    setEditName(member.full_name || "");
    setEditPassword("");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <PageHeader
        title="Usuários"
        subtitle="Gerencie os membros da sua agência"
        actions={
          isAdmin && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Novo Membro
            </Button>
          )
        }
      />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {members.map((member) => (
          <Card key={member.id}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center gap-3">
                <UserCircle className="h-8 w-8 text-muted-foreground" />
                <div>
                  <CardTitle className="text-lg font-bold">
                    {member.full_name || "Sem nome"}
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2 text-sm">
                {member.email && (
                  <div>
                    <span className="font-medium">Email:</span> {member.email}
                  </div>
                )}
                <div>
                  <span className="font-medium">Perfil:</span>{" "}
                  <Badge variant="default">
                    {member.access_profile_name || "Sem perfil"}
                  </Badge>
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(member)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setDeactivatingMember(member)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Desativar
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {members.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          Nenhum membro encontrado
        </div>
      )}

      {/* Modal Criar Membro */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Novo Membro</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="createName">Nome Completo</Label>
              <Input
                id="createName"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Nome completo"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="createEmail">Email</Label>
              <Input
                id="createEmail"
                type="email"
                value={createEmail}
                onChange={(e) => setCreateEmail(e.target.value)}
                placeholder="email@exemplo.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="createPassword">Senha</Label>
              <Input
                id="createPassword"
                type="password"
                value={createPassword}
                onChange={(e) => setCreatePassword(e.target.value)}
                placeholder="Senha inicial"
                required
                minLength={6}
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating ? "Criando..." : "Criar Membro"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Editar Membro */}
      <Dialog open={!!editingMember} onOpenChange={() => setEditingMember(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Editar Membro</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editName">Nome Completo</Label>
              <Input
                id="editName"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Nome completo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editPassword">Nova Senha (deixe vazio para manter)</Label>
              <Input
                id="editPassword"
                type="password"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                placeholder="Nova senha"
                minLength={6}
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditingMember(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isEditing}>
                {isEditing ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirmação de Desativação */}
      <AlertDialog open={!!deactivatingMember} onOpenChange={() => setDeactivatingMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar membro</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja desativar o acesso de{" "}
              <strong>{deactivatingMember?.full_name}</strong>? O usuário perderá acesso ao sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivate}
              disabled={isDeactivating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeactivating ? "Desativando..." : "Desativar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
